import { type I18nLanguage, type localeConfigurations } from '@/i18n';
import type { TenantName, TenantOption } from '@/types/tenant';
import { TENANT_NAMES } from '@/types/tenant';
import type { StopsSearchingStrategy } from '@packages/webshop-search-widget';
import type { Country } from 'react-phone-number-input';

// Keyed by `TenantName` so adding a tenant to the central enum forces a
// label to be added here too.
const TENANT_LABELS: Record<TenantName, string> = {
  ci: 'Turnit',
  arenaways: 'Arenaways',
  twiliner: 'Twiliner',
  velvet: 'Velvet',
  aguia: 'Aguia',
  'sandbox-fs': 'Fs',
  tifrance: 'TI France',
  maas: 'MaaS',
  hellenictrain: 'Hellenic Train',
  connectbus: 'ConnectBus',
  ltglink: 'LTGLink',
  sandbox: 'Sandbox',
};

const TENANT_OPTIONS: readonly TenantOption[] = TENANT_NAMES.map((value) => ({
  value,
  label: TENANT_LABELS[value],
}));

const HOSTNAME_TENANT_MAP: Array<{
  hostnameIncludes: string;
  tenant: TenantOption['value'];
}> = [
  {
    hostnameIncludes: 'webshop-engine.demos.sandbox-brazil.turnit.tech',
    tenant: 'aguia',
  },
  {
    hostnameIncludes: 'webshop-engine.demos.sandbox-fs.turnit.tech',
    tenant: 'sandbox-fs',
  },
  {
    hostnameIncludes: 'webshop-engine.dev.maas.turnit.tech',
    tenant: 'maas',
  },
];

export const getInitialTenant = (): TenantOption => {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : undefined;

  const hostMatchedTenant = hostname
    ? HOSTNAME_TENANT_MAP.find(({ hostnameIncludes }) =>
        hostname.includes(hostnameIncludes)
      )?.tenant
    : undefined;

  const value = hostMatchedTenant ?? import.meta.env.VITE_APP_TENANT;
  const knownLabel = TENANT_OPTIONS.find(
    (option) => option.value === value
  )?.label;

  return { value, label: knownLabel ?? value };
};

const stripWrappingQuotes = (value: string): string => {
  let result = value.trim();
  while (result.length >= 2 && /^(['"]).*\1$/.test(result)) {
    result = result.slice(1, -1).trim();
  }
  return result;
};

const readEnvString = (name: string): string | undefined => {
  const value: unknown = import.meta.env[name];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('#{')) {
    return undefined;
  }
  return stripWrappingQuotes(trimmed) || undefined;
};

const readEnvBoolean = (name: string): boolean | undefined => {
  const value = readEnvString(name)?.toLowerCase();
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
};

const readEnvOneOf = <T extends string>(
  name: string,
  allowed: readonly T[]
): T | undefined => {
  const value = readEnvString(name);
  return allowed.includes(value as T) ? (value as T) : undefined;
};

const I18N_LANGUAGES: readonly I18nLanguage[] = [
  'en',
  'it',
  'de',
  'pt',
  'fr',
  'el',
  'et',
];

const readEnvLanguageList = (name: string): I18nLanguage[] | undefined => {
  const value = readEnvString(name);
  if (!value) {
    return undefined;
  }
  const entries = value
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .split(',')
    .map((entry) => stripWrappingQuotes(entry))
    .filter(Boolean);
  const valid = entries.filter((entry): entry is I18nLanguage =>
    (I18N_LANGUAGES as readonly string[]).includes(entry)
  );
  return valid.length > 0 && valid.length === entries.length
    ? valid
    : undefined;
};

const readEnvCountry = (name: string): Country | undefined => {
  const value = readEnvString(name)?.toUpperCase();
  return value && /^[A-Z]{2}$/.test(value) ? (value as Country) : undefined;
};

// Octopus requires every variable to have a default value, so deployments
// without a container configured ship "0" — treat it as unset.
const readEnvGtmContainerId = (name: string): string | undefined => {
  const value = readEnvString(name);
  return value === '0' ? undefined : value;
};

export type TenantConfiguration = {
  language: I18nLanguage;
  languageOptions: Array<keyof typeof localeConfigurations>;
  defaultCountry: Country;
  webPageUrl?: string;
  showTravelPassDisclaimer: boolean;
  showSuccessMessage: boolean;
  showPayWithExternalPayment: boolean;
  paymentClassificationGroup: 'PAYMENT_TYPE' | 'SALES_POINT_PAYMENT_TYPE';
  stopsSearchingStrategy: StopsSearchingStrategy;
  showLogin: boolean;
  showCheckoutExtras: boolean;
  showCheckoutConsents: boolean;
  showAccountMenuVouchers: boolean;
  showAccountMenuTravelPass: boolean;
  showMakeChanges: boolean;
  showOffersAdvancedLayout: boolean;
  enableTaraLogin?: boolean;
  enableGoogleTagManager: boolean;
  gtmContainerId?: string;
  enableConsentMode?: boolean;
  enableMapComponent: boolean;
  enableJourneyCardTimeline?: boolean;
  enableSearchWidgetTimePicker?: boolean;
  // DS-backed tenants: the distribution system cannot exchange the outbound
  // and return trip in one operation, so the change-trips selection must be
  // limited to a single direction at a time (BR-56612)
  restrictChangeToSingleDirection?: boolean;
};

type RequiredEnvField<Key extends keyof TenantConfiguration> = {
  key: Key;
  envName: string;
  read: (envName: string) => TenantConfiguration[Key] | undefined;
};

const requiredField = <Key extends keyof TenantConfiguration>(
  key: Key,
  envName: string,
  read: (envName: string) => TenantConfiguration[Key] | undefined
): RequiredEnvField<Key> => ({ key, envName, read });

const REQUIRED_FIELDS = [
  requiredField('language', 'VITE_APP_DEFAULT_LANGUAGE', (name) =>
    readEnvOneOf(name, I18N_LANGUAGES)
  ),
  requiredField('languageOptions', 'VITE_APP_LANGUAGE_OPTIONS', (name) =>
    readEnvLanguageList(name)
  ),
  requiredField('defaultCountry', 'VITE_APP_DEFAULT_COUNTRY', (name) =>
    readEnvCountry(name)
  ),
  requiredField(
    'showTravelPassDisclaimer',
    'VITE_APP_SHOW_TRAVEL_PASS_DISCLAIMER',
    (name) => readEnvBoolean(name)
  ),
  requiredField('showSuccessMessage', 'VITE_APP_SHOW_SUCCESS_MESSAGE', (name) =>
    readEnvBoolean(name)
  ),
  requiredField(
    'showPayWithExternalPayment',
    'VITE_APP_SHOW_PAY_WITH_EXTERNAL_PAYMENT',
    (name) => readEnvBoolean(name)
  ),
  requiredField(
    'paymentClassificationGroup',
    'VITE_APP_PAYMENT_CLASSIFICATION_GROUP',
    (name) =>
      readEnvOneOf(name, ['PAYMENT_TYPE', 'SALES_POINT_PAYMENT_TYPE'] as const)
  ),
  requiredField(
    'stopsSearchingStrategy',
    'VITE_APP_STOP_SEARCHING_STRATEGY',
    (name) =>
      readEnvOneOf(name, [
        'dynamic',
        'static',
        'dynamicStopsAndAddresses',
      ] as const)
  ),
  requiredField('showLogin', 'VITE_APP_SHOW_LOGIN', (name) =>
    readEnvBoolean(name)
  ),
  requiredField('showCheckoutExtras', 'VITE_APP_SHOW_CHECKOUT_EXTRAS', (name) =>
    readEnvBoolean(name)
  ),
  requiredField(
    'showCheckoutConsents',
    'VITE_APP_SHOW_CHECKOUT_CONSENTS',
    (name) => readEnvBoolean(name)
  ),
  requiredField(
    'showAccountMenuVouchers',
    'VITE_APP_SHOW_ACCOUNT_MENU_VOUCHERS',
    (name) => readEnvBoolean(name)
  ),
  requiredField(
    'showAccountMenuTravelPass',
    'VITE_APP_SHOW_ACCOUNT_MENU_TRAVEL_PASS',
    (name) => readEnvBoolean(name)
  ),
  requiredField('showMakeChanges', 'VITE_APP_SHOW_MAKE_CHANGES', (name) =>
    readEnvBoolean(name)
  ),
  requiredField(
    'showOffersAdvancedLayout',
    'VITE_APP_SHOW_OFFERS_ADVANCED_LAYOUT',
    (name) => readEnvBoolean(name)
  ),
  requiredField(
    'enableGoogleTagManager',
    'VITE_APP_ENABLE_GOOGLE_TAG_MANAGER',
    (name) => readEnvBoolean(name)
  ),
  requiredField('enableMapComponent', 'VITE_APP_ENABLE_MAP_COMPONENT', (name) =>
    readEnvBoolean(name)
  ),
] as const;

const OPTIONAL_FIELDS: {
  [Key in keyof TenantConfiguration]?: () => TenantConfiguration[Key];
} = {
  webPageUrl: () => readEnvString('VITE_APP_WEB_PAGE_URL'),
  enableTaraLogin: () => readEnvBoolean('VITE_APP_ENABLE_TARA_LOGIN'),
  gtmContainerId: () => readEnvGtmContainerId('VITE_APP_GTM_CONTAINER_ID'),
  enableConsentMode: () => readEnvBoolean('VITE_APP_ENABLE_CONSENT_MODE'),
  enableJourneyCardTimeline: () =>
    readEnvBoolean('VITE_APP_ENABLE_JOURNEY_CARD_TIMELINE'),
  enableSearchWidgetTimePicker: () =>
    readEnvBoolean('VITE_APP_ENABLE_SEARCH_WIDGET_TIME_PICKER'),
  restrictChangeToSingleDirection: () =>
    readEnvBoolean('VITE_APP_RESTRICT_CHANGE_TO_SINGLE_DIRECTION'),
};

let cachedTenantConfiguration: TenantConfiguration | undefined;

export const getTenantConfiguration = (): TenantConfiguration => {
  if (cachedTenantConfiguration) {
    return cachedTenantConfiguration;
  }

  const missing: string[] = [];
  const configuration: Record<string, unknown> = {};

  for (const field of REQUIRED_FIELDS) {
    const value = field.read(field.envName);
    if (value === undefined) {
      missing.push(field.envName);
    } else {
      configuration[field.key] = value;
    }
  }

  if (missing.length > 0) {
    throw new Error(
      `Missing or unresolved tenant configuration environment variable(s): ${missing.join(', ')}`
    );
  }

  for (const key of Object.keys(OPTIONAL_FIELDS) as Array<
    keyof typeof OPTIONAL_FIELDS
  >) {
    const value = OPTIONAL_FIELDS[key]?.();
    if (value !== undefined) {
      configuration[key] = value;
    }
  }

  cachedTenantConfiguration = configuration as TenantConfiguration;
  return cachedTenantConfiguration;
};
